import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

import ArenaCanvas from '../components/arena/ArenaCanvas';
import Toolbar from '../components/arena/Toolbar';
import StatsPanel from '../components/arena/StatsPanel';
import FlexZonePanel, { M_TO_PX } from '../components/arena/FlexZonePanel';
import StagePanel from '../components/arena/StagePanel';
import SectorList from '../components/arena/SectorList';

import { DEFAULT_ARENA } from '../data/defaultArena';
import { exportToExcel, exportToCSV, exportToPDF } from '../lib/exportUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArenaPage() {
  const [removedSeats, setRemovedSeats] = useState({});
  const [flexZones, setFlexZones] = useState([]);
  const [selectedFlexId, setSelectedFlexId] = useState(null);
  const [stages, setStages] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [selectMode, setSelectMode] = useState('click');
  const [configName, setConfigName] = useState('Kanizsa Aréna');
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [showHitboxes, setShowHitboxes] = useState(false);

  // Sectors are read-only - extracted from the PDF source of truth
  const sectors = DEFAULT_ARENA.sectors;
  const arena = useMemo(() => DEFAULT_ARENA, []);

  const updateFlexZone = (id, patch) =>
    setFlexZones((prev) => prev.map((z) => (z.id === id ? { ...z, ...patch } : z)));

  const updateStage = (id, patch) =>
    setStages((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const addStageFromRect = ({ x, y, widthPx, heightPx }) => {
    const id = `stage-${Date.now()}`;
    const newStage = {
      id,
      name: `Színpad ${stages.length + 1}`,
      widthM: 12,
      heightM: 6,
      widthPx,
      heightPx,
      x,
      y,
    };
    setStages((prev) => [...prev, newStage]);
    setSelectedStageId(id);
    toast.success(`Új színpad létrehozva — add meg a méreteit a panelben!`);
  };

  const addFlexZoneFromRect = ({ x, y, widthPx, heightPx }) => {
    // A térképen rajzolt méret pixel-ben rögzül; a méter értéket a felhasználó utólag állítja be.
    const id = `flex-${Date.now()}`;
    const widthM = 10;
    const heightM = 10;
    const newZone = {
      id,
      name: `Zóna ${flexZones.length + 1}`,
      widthM,
      heightM,
      peoplePerSqm: 4,
      widthPx,
      heightPx,
      x,
      y,
    };
    setFlexZones((prev) => [...prev, newZone]);
    setSelectedFlexId(id);
    toast.success(`Új flex zóna létrehozva — add meg a méreteit a panelben!`);
  };

  const handleResetRemoved = () => {
    setRemovedSeats({});
    toast.success('Minden szék visszaállítva');
  };

  const handleExportXLSX = () => {
    exportToExcel(sectors, removedSeats, flexZones, configName);
    toast.success('Excel export sikeres');
  };

  const handleExportCSV = () => {
    exportToCSV(sectors, removedSeats, configName);
    toast.success('CSV export sikeres');
  };

  const handleExportPDF = async () => {
    try {
      toast.info('PDF generálás folyamatban…');
      await exportToPDF(sectors, removedSeats, flexZones, stages, configName);
      toast.success('PDF export sikeres');
    } catch (e) {
      console.error(e);
      toast.error('PDF export sikertelen');
    }
  };

  const handleSaveOpen = () => setSaveOpen(true);

  const doSave = async () => {
    try {
      const payload = {
        name: configName,
        config: { stage: DEFAULT_ARENA.stage, viewBox: DEFAULT_ARENA.viewBox, stages },
        removed_seats: removedSeats,
        flex_zones: flexZones,
      };
      await axios.post(`${API}/configs`, payload);
      toast.success('Konfiguráció elmentve');
      setSaveOpen(false);
    } catch (e) {
      toast.error('Mentés sikertelen');
    }
  };

  const handleLoadOpen = async () => {
    try {
      const res = await axios.get(`${API}/configs`);
      setSavedList(res.data || []);
      setLoadOpen(true);
    } catch (e) {
      toast.error('Lista betöltés sikertelen');
    }
  };

  const doLoad = (cfg) => {
    setRemovedSeats(cfg.removed_seats || {});
    setFlexZones(cfg.flex_zones || []);
    setStages(cfg.config?.stages || []);
    setConfigName(cfg.name || 'Betöltött');
    setLoadOpen(false);
    toast.success(`"${cfg.name}" betöltve`);
  };

  const handleDeleteConfig = async (id) => {
    try {
      await axios.delete(`${API}/configs/${id}`);
      setSavedList((prev) => prev.filter((c) => c.id !== id));
      toast.success('Törölve');
    } catch (e) {
      toast.error('Törlés sikertelen');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-stone-100" data-testid="arena-page">
      <Toaster position="top-right" richColors />

      <header className="flex items-center justify-between px-5 py-3 bg-stone-900 text-stone-50 border-b border-stone-800" data-testid="app-header">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center">
            <span className="text-stone-900 font-black text-sm">K</span>
          </div>
          <div>
            <h1 className="text-base font-bold tracking-tight leading-tight">Kanizsa Aréna</h1>
            <p className="text-[10px] text-stone-400 tracking-wider uppercase">Szék manager • Jegy tükör</p>
          </div>
        </div>
        <Input
          value={configName}
          onChange={(e) => setConfigName(e.target.value)}
          className="max-w-xs h-9 bg-stone-800 border-stone-700 text-stone-100 text-sm"
          placeholder="Konfiguráció neve"
          data-testid="config-name-input"
        />
      </header>

      <Toolbar
        selectMode={selectMode}
        setSelectMode={setSelectMode}
        onResetRemoved={handleResetRemoved}
        onExportXLSX={handleExportXLSX}
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        onSave={handleSaveOpen}
        onLoad={handleLoadOpen}
        showHitboxes={showHitboxes}
        setShowHitboxes={setShowHitboxes}
      />

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative">
          <ArenaCanvas
            arena={arena}
            removedSeats={removedSeats}
            setRemovedSeats={setRemovedSeats}
            flexZones={flexZones}
            selectedFlexId={selectedFlexId}
            setSelectedFlexId={setSelectedFlexId}
            updateFlexZone={updateFlexZone}
            addFlexZoneFromRect={addFlexZoneFromRect}
            stages={stages}
            selectedStageId={selectedStageId}
            setSelectedStageId={setSelectedStageId}
            updateStage={updateStage}
            addStageFromRect={addStageFromRect}
            selectMode={selectMode}
            showHitboxes={showHitboxes}
          />
        </div>

        <aside className="w-[340px] border-l border-stone-200 bg-stone-50 overflow-y-auto p-4 space-y-4" data-testid="sidebar">
          <StatsPanel sectors={sectors} removedSeats={removedSeats} flexZones={flexZones} />
          <FlexZonePanel
            flexZones={flexZones}
            setFlexZones={setFlexZones}
            selectedFlexId={selectedFlexId}
            setSelectedFlexId={setSelectedFlexId}
          />
          <StagePanel
            stages={stages}
            setStages={setStages}
            selectedStageId={selectedStageId}
            setSelectedStageId={setSelectedStageId}
          />
          <SectorList sectors={sectors} removedSeats={removedSeats} setRemovedSeats={setRemovedSeats} />
        </aside>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-dialog">
          <DialogHeader>
            <DialogTitle>Konfiguráció mentése</DialogTitle>
            <DialogDescription>Adj egyedi nevet a jelenlegi beállításnak.</DialogDescription>
          </DialogHeader>
          <Label className="text-xs">Név</Label>
          <Input value={configName} onChange={(e) => setConfigName(e.target.value)} data-testid="save-name-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Mégse</Button>
            <Button onClick={doSave} data-testid="confirm-save-btn">Mentés</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={loadOpen} onOpenChange={setLoadOpen}>
        <DialogContent data-testid="load-dialog">
          <DialogHeader>
            <DialogTitle>Mentett konfigurációk</DialogTitle>
            <DialogDescription>Válassz egy korábbi mentést a betöltéshez.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-1.5">
            {savedList.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-8">Még nincs mentett konfiguráció.</p>
            ) : (
              savedList.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between p-3 border border-stone-200 rounded-md hover:bg-stone-50"
                  data-testid={`saved-config-${c.id}`}
                >
                  <div>
                    <div className="text-sm font-bold">{c.name}</div>
                    <div className="text-[10px] text-stone-500">{new Date(c.created_at).toLocaleString('hu-HU')}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" onClick={() => doLoad(c)} data-testid={`load-btn-${c.id}`}>Betölt</Button>
                    <Button size="sm" variant="outline" onClick={() => handleDeleteConfig(c.id)}>Töröl</Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
